import * as React from 'react';

import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {SelectionList} from 'shells/selection-modal/selection-list';

import type {GetPotentialActivityEntities_activityPotentialEntities_participants as Participant} from 'nutshell-graphql-types';

import type {Option} from '../../../core/select/graphql-select-inline';
import {GraphQLSelectParticipants} from '../../../core/select/graphql-select-participants';

import {useActivityCreateOrMergeContactFromExternalAttendee} from '../../graphql/hooks/use-activity-create-or-merge-contact-from-external-attendee';

import {
    mapListOptionsToSelectOptions,
    mapParticipantsToSelectOptions,
    mapSelectOptionsToListOptions,
    sortParticipantsOnAdd,
} from './helpers';

import styles from './calendar.module.css';

// Type should be ActivityCreateOrMergeContactFromExternalAttendeeInput minus the activityId
export type ExternalAttendeeToContactData = {
    externalAttendeeId: string;
    name?: string | null | undefined;
    email?: string | null | undefined;
    phone?: string | null | undefined;
    contactId?: string | null | undefined;
};

type Props = {
    activityId?: string;
    selectedParticipants: Option[];
    onChange: (options: Option | Option[], preventFromStatusChange?: boolean) => void;
    potentialParticipants?: Participant[];
    isReadOnly?: boolean;
};

export function ParticipantsSection(props: Props) {
    const {selectedParticipants, onChange, potentialParticipants = [], isReadOnly = false} = props;

    const {
        activityCreateOrMergeContactFromExternalAttendee,
        isLoading: isCreatingOrMergingContact,
    } = useActivityCreateOrMergeContactFromExternalAttendee();

    const handleCreateContactFromExternalAttendee = React.useCallback(
        (data: ExternalAttendeeToContactData) => {
            // Find the external attendee in current selected participants so
            // that we can replace it with the created contact on mutation
            // success
            const externalAttendeeIndex = selectedParticipants.findIndex(
                (participant) => participant.id === data.externalAttendeeId
            );

            if (externalAttendeeIndex === -1 || !props.activityId) {
                return;
            }

            activityCreateOrMergeContactFromExternalAttendee({
                activityId: props.activityId,
                externalAttendeeId: data.externalAttendeeId,
                name: data.name,
                email: data.email,
                phone: data.phone,
                contactId: data.contactId,
            }).then((result) => {
                if (
                    result.data &&
                    result.data.activityCreateOrMergeContactFromExternalAttendee &&
                    result.data.activityCreateOrMergeContactFromExternalAttendee.contact
                ) {
                    const contact = {
                        ...result.data.activityCreateOrMergeContactFromExternalAttendee.contact,
                        __typename: 'Contact',
                    } as const;

                    const newContactOption = mapParticipantsToSelectOptions([contact])[0];

                    const updatedParticipants = [...selectedParticipants];

                    const contactExistsElsewhere = updatedParticipants.some(
                        (participant, index) =>
                            // @ts-expect-error
                            participant.id === newContactOption.id &&
                            index !== externalAttendeeIndex
                    );

                    if (contactExistsElsewhere) {
                        updatedParticipants.splice(externalAttendeeIndex, 1);
                    } else {
                        // @ts-expect-error
                        updatedParticipants[externalAttendeeIndex] = newContactOption;
                    }

                    onChange(updatedParticipants, true);
                }
            });
        },
        [
            selectedParticipants,
            props.activityId,
            activityCreateOrMergeContactFromExternalAttendee,
            onChange,
        ]
    );

    const defaultOptions = mapParticipantsToSelectOptions(potentialParticipants);
    const selectedOptionsForList = mapSelectOptionsToListOptions(
        selectedParticipants,
        handleCreateContactFromExternalAttendee,
        isCreatingOrMergingContact,
        isReadOnly
    );

    return (
        <IconSection
            icon={PeopleRegularIcon}
            header={isReadOnly ? 'Participants' : undefined}
            marginTopValue={isReadOnly ? 2 : undefined}
        >
            <div className={styles['participants-container']}>
                {!isReadOnly && (
                    <GraphQLSelectParticipants
                        selectedValues={selectedParticipants}
                        onChange={(newOptions) => {
                            onChange(sortParticipantsOnAdd(newOptions));
                        }}
                        isMulti={true}
                        searchable={true}
                        defaultOptions={defaultOptions.length !== 0 ? defaultOptions : true}
                        controlShouldRenderValue={false}
                        showEmail={true}
                        triggerChangeOnBlur={false}
                    />
                )}
                <SelectionList
                    selectedOptions={selectedOptionsForList}
                    setSelectedOptions={(options) => {
                        onChange(mapListOptionsToSelectOptions(options));
                    }}
                    nothingFoundState=''
                    hasBottomBorder={false}
                    shouldListFitContent={true}
                    smallLabel={true}
                    canRemove={!isReadOnly}
                    maxDisplayedBeforeSeeMore={3}
                />
            </div>
        </IconSection>
    );
}
